import type { RefObject } from 'react';

// Note: This file is used both in the browser app and imported by Playwright E2E code.
// Some E2E tsconfigs don't include the DOM lib, so we keep the types DOM-lib-agnostic.
export type TestIdElement = {
  setAttribute?: (name: string, value: string) => void;
  removeAttribute?: (name: string) => void;
  querySelector?: (selector: string) => unknown;
  querySelectorAll?: (selector: string) => Iterable<unknown>;
  textContent?: string | null;
};

export const toTestIdSlug = (value: string | null | undefined): string => {
  if (!value) return '';
  return value
    .normalize('NFD')
    .replace(/[\u0300-\u036f]/g, '')
    .toLowerCase()
    .replace(/[^a-z0-9]/g, '');
};

export const getLocationPositionKey = (
  locationId: string | number | null | undefined,
  locationIndex: number,
  totalLocations: number,
): string => {
  if (locationIndex <= 0) return 'locationFirst';
  if (locationIndex >= totalLocations - 1) return 'locationLast';
  return `locationMid${String(locationIndex)}`;
};

export const stampPrimeDropdownOverlayTestIds = (
  listTestId: string | null | undefined,
  options?: {
    panelClassName?: string;
    itemSelector?: string;
    overlaySuffix?: string;
  },
) => {
  if (!listTestId) return;

  const panelClassName = options?.panelClassName ?? 'expeditionInfoDropdownPanel';
  const itemSelector = options?.itemSelector ?? 'li.p-dropdown-item';
  const overlaySuffix = options?.overlaySuffix ?? '-overlay';

  const doc = (globalThis as any)?.document as any;
  const panels = Array.from(doc?.querySelectorAll?.(`.p-dropdown-panel.${panelClassName}`) ?? []) as any[];
  const panel = panels.length ? (panels[panels.length - 1] as TestIdElement) : null;
  if (!panel?.setAttribute) return;

  panel.setAttribute('data-testid', `${listTestId}${overlaySuffix}`);

  const items = Array.from((panel as any)?.querySelectorAll?.(itemSelector) ?? []) as TestIdElement[];
  for (const item of items) {
    const text = (item?.textContent || '').trim();
    const slug = toTestIdSlug(text);
    if (slug) {
      item?.setAttribute?.('data-testid', `${listTestId}-${slug}`);
    }
  }
};

export const setOrRemoveDataTestId = (
  element: TestIdElement | null | undefined,
  dataTestId: string | null | undefined,
) => {
  if (!element) return;

  if (dataTestId) {
    element.setAttribute?.('data-testid', dataTestId);
  } else {
    element.removeAttribute?.('data-testid');
  }
};

export const stampDataTestIdOnDescendant = (
  wrapper: TestIdElement | null | undefined,
  selector: string,
  dataTestId: string | null | undefined,
) => {
  const target = (wrapper?.querySelector?.(selector) as TestIdElement | null) ?? null;
  setOrRemoveDataTestId(target, dataTestId);
};

export const stampDataTestIdOnDescendantRef = (
  wrapperRef: RefObject<TestIdElement | null>,
  selector: string,
  dataTestId: string | null | undefined,
) => {
  stampDataTestIdOnDescendant(wrapperRef.current, selector, dataTestId);
};

export const deferStampDataTestIdOnDescendantRef = (
  wrapperRef: RefObject<TestIdElement | null>,
  selector: string,
  dataTestId: string | null | undefined,
  delayMs = 0,
): (() => void) => {
  const timer = setTimeout(() => {
    stampDataTestIdOnDescendantRef(wrapperRef, selector, dataTestId);
  }, delayMs);

  return () => clearTimeout(timer);
};
